import { useEffect, useRef, useCallback } from 'react';
import { useStore } from '../store';

const API_URL = import.meta.env.VITE_API_URL || '';
const HEARTBEAT_INTERVAL = 60000; // 60 seconds

/**
 * Hook to send activity heartbeats to the server.
 * Tracks user activity (mouse, keyboard, scroll, click) and sends
 * periodic heartbeats when the user is logged in.
 */
export function useActivityHeartbeat() {
  const { auth, me } = useStore();
  const lastActivityRef = useRef(Date.now());
  const isActiveRef = useRef(true);

  // Track user activity
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    isActiveRef.current = true;
  }, []);

  // Send heartbeat to server
  const sendHeartbeat = useCallback(async () => {
    if (!auth.isAuthenticated || !auth.user) return;
    
    // Don't send if user has disabled activity sharing
    if (me.showActivityStatus === false) return;

    try {
      const response = await fetch(`${API_URL}/activity/heartbeat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // In a real app, you'd include the auth token here
          // 'Authorization': `Bearer ${auth.token}`
        },
        body: JSON.stringify({
          lastActivity: lastActivityRef.current,
        }),
      });

      if (!response.ok) {
        console.warn('Heartbeat failed:', response.status);
      }
    } catch (error) {
      // Silently fail - presence is not critical
      console.debug('Heartbeat error:', error);
    }
  }, [auth.isAuthenticated, auth.user, me.showActivityStatus]);

  useEffect(() => {
    if (!auth.isAuthenticated) return;

    // Track activity events
    const events = ['mousemove', 'keydown', 'scroll', 'click', 'touchstart'];
    events.forEach((event) => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    // Track visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        updateActivity();
        sendHeartbeat(); // Send heartbeat when tab becomes visible
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Send initial heartbeat
    sendHeartbeat();

    // Send heartbeats periodically
    const interval = setInterval(() => {
      // Only send if tab is visible
      if (document.visibilityState === 'visible') {
        sendHeartbeat();
      }
    }, HEARTBEAT_INTERVAL);

    // Cleanup
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, updateActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(interval);
    };
  }, [auth.isAuthenticated, updateActivity, sendHeartbeat]);

  return {
    lastActivity: lastActivityRef.current,
    isActive: isActiveRef.current,
  };
}

/**
 * Hook to fetch and update activity status for shared teammates.
 * Polls the server periodically to get real-time presence data.
 */
export function useActivityStatus() {
  const { auth, people, updatePeopleActivityStatus } = useStore();

  const fetchActivityStatus = useCallback(async () => {
    if (!auth.isAuthenticated) return;

    // Get only shared (real) users
    const sharedPeople = people.filter((p) => p.source === 'shared');
    if (sharedPeople.length === 0) return;

    const userIds = sharedPeople.map((p) => p.id).join(',');

    try {
      const response = await fetch(
        `${API_URL}/activity/status?userIds=${userIds}`,
        {
          headers: {
            'Content-Type': 'application/json',
            // 'Authorization': `Bearer ${auth.token}`
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.statuses) {
          updatePeopleActivityStatus(data.statuses);
        }
      }
    } catch (error) {
      console.debug('Activity status fetch error:', error);
    }
  }, [auth.isAuthenticated, people, updatePeopleActivityStatus]);

  useEffect(() => {
    if (!auth.isAuthenticated) return;

    // Initial fetch
    fetchActivityStatus();

    // Poll every 30 seconds
    const interval = setInterval(fetchActivityStatus, 30000);

    return () => clearInterval(interval);
  }, [auth.isAuthenticated, fetchActivityStatus]);
}
